import Checkbox from '@orn/shared-web/components/Checkbox/Checkbox';
import { NativeSelect } from '@orn/shared-web/components/Select';
import TextArea from '@orn/shared-web/components/Textarea';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { useClipboard } from '@orn/shared-web/hooks/useClipboard';
import { CheckIcon, CopyIcon } from '@orn/shared-web/icons';
import filterNullsAndUndefined from '@orn/shared-web/utils/filterNullsAndUndefined';
import formatCalendarDate from '@orn/shared-web/utils/formatCalendarDate';
import toCurrency from '@orn/shared-web/utils/toCurrency';
import * as React from 'react';

import Table from '../../components/Table';

import { PRIORITY_ORDER, resolvePriority, type TransactionNode } from './transactionAnomalyFeedbackTypes';

import type { TransactionAnomalyPriority as TransactionAnomalyPriorityEnum } from '../../__generated__/globalTypes';

interface ITransactionRowProps {
  transaction: TransactionNode;
  merchantStatsById: Map<string, Record<string, unknown>>;
  detailedReasonMap: Map<string, string>;
  onDetailedReasonChange: (transactionId: string, value: string) => void;
  onToggleReviewed: (transaction: TransactionNode, checked: boolean) => Promise<void>;
  onSavePriority: (
    transactionId: string,
    priority: TransactionAnomalyPriorityEnum,
    detailedReason: string | null
  ) => Promise<void>;
}

function getNumberOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

function formatMatch(value: unknown): string {
  if (value === 1) {
    return 'Yes';
  }
  if (value === -1) {
    return 'No';
  }
  return 'n/a';
}

/**
 * Single-row representation of a flagged transaction in the review table.
 * Hosts the reviewed-checkbox, priority cell (priority + detailed reason),
 * and read-only context columns (score, reasons + score-parts breakdown,
 * date, description, amount, global stats, merchant global stats, merchant
 * stats). Stateless aside from the controlled detailed-reason input.
 */
const TransactionRow = ({
  transaction,
  merchantStatsById,
  detailedReasonMap,
  onDetailedReasonChange,
  onToggleReviewed,
  onSavePriority,
}: ITransactionRowProps) => {
  const score = transaction.anomalyDetection?.anomalyScore ?? null;
  const reasons = transaction.anomalyDetection?.anomalyReasons ?? [];
  const scoreParts = transaction.anomalyDetection?.scoreParts;
  const amount = typeof transaction.amount === 'string' ? parseFloat(transaction.amount) : transaction.amount;
  const merchantStats = transaction.merchant?.id ? merchantStatsById.get(transaction.merchant.id) : undefined;
  const isReviewed = !!transaction.anomalyFeedback;
  const filteredJson = transaction.rawJson ? filterNullsAndUndefined(transaction.rawJson) : undefined;

  const features = (transaction.anomalyDetection?.featuresJson ?? undefined) as Record<string, unknown> | undefined;
  const globalPercentile = getNumberOrUndefined(features?.percentile);
  const globalDeltaMedianPct = getNumberOrUndefined(features?.percent_deviation_from_median);
  const merchantPercentile = getNumberOrUndefined(features?.merchant_percentile);
  const merchantDeltaMedianPct = getNumberOrUndefined(features?.merchant_percent_deviation_from_median);
  const merchantCount = getNumberOrUndefined(features?.merchant_count);
  const dayOfMonthMatch = formatMatch(features?.day_of_month_match);
  const financialAccountMatch = formatMatch(features?.financial_account_match);

  const detailedReason = detailedReasonMap.get(transaction.id) ?? transaction.anomalyFeedback?.detailedReason ?? '';

  return (
    <>
      <Table.Row className="border-b-0">
        <Table.Cell>
          <Checkbox
            isSelected={isReviewed}
            onChange={(checked) => {
              void onToggleReviewed(transaction, checked);
            }}
            aria-label={`Mark transaction ${transaction.id} reviewed`}
          />
        </Table.Cell>
        <Table.Cell mono>{score !== null ? score.toFixed(2) : 'N/A'}</Table.Cell>
        <Table.Cell muted>
          <div className="flex flex-col gap-2">
            {reasons.length > 0 ? (
              <ul className="flex flex-col gap-0.5 text-xs">
                {reasons.map((reason, idx) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>
            ) : (
              <span className="text-fg-quaternary">—</span>
            )}
            {scoreParts ? <ScoreBreakdown parts={scoreParts} /> : null}
          </div>
        </Table.Cell>
        <Table.Cell muted>
          {filteredJson ? (
            <Tooltip
              title="Raw JSON"
              placement="right"
              wide
              description={
                <pre className="wrap-break-word whitespace-pre-wrap text-xs">
                  {JSON.stringify(filteredJson, null, 2)}
                </pre>
              }
            >
              <TooltipTrigger>
                <span className="cursor-help underline decoration-dotted">{formatCalendarDate(transaction.date)}</span>
              </TooltipTrigger>
            </Tooltip>
          ) : (
            formatCalendarDate(transaction.date)
          )}
        </Table.Cell>
        <Table.Cell>
          <DescriptionCell transaction={transaction} />
        </Table.Cell>
        <Table.Cell mono className="text-right">
          {toCurrency(amount)}
        </Table.Cell>
        <Table.Cell muted className="text-xs">
          {globalPercentile != null && globalDeltaMedianPct != null ? (
            <div className="flex flex-col gap-0.5">
              <StatLine label="Percentile" value={`${globalPercentile.toFixed(1)}%`} />
              <StatLine label="Δ Median %" value={`${globalDeltaMedianPct.toFixed(1)}%`} />
            </div>
          ) : (
            <span className="text-fg-quaternary">N/A</span>
          )}
        </Table.Cell>
        <Table.Cell muted className="text-xs">
          {merchantStats ? (
            <div className="flex flex-col gap-0.5">
              <StatLine label="Median" value={toCurrency((merchantStats.median as number) ?? 0)} />
              <StatLine label="Count" value={String((merchantStats.count as number) ?? 0)} />
            </div>
          ) : (
            <span className="text-fg-quaternary">No merchant stats</span>
          )}
        </Table.Cell>
        <Table.Cell muted className="text-xs">
          {merchantPercentile != null && merchantDeltaMedianPct != null ? (
            <div className="flex flex-col gap-0.5">
              <StatLine label="Percentile" value={`${merchantPercentile.toFixed(1)}%`} />
              <StatLine label="Δ Median %" value={`${merchantDeltaMedianPct.toFixed(1)}%`} />
              {merchantCount !== undefined ? <StatLine label="Count" value={String(merchantCount)} /> : null}
              <StatLine label="Day-of-month" value={dayOfMonthMatch} />
              <StatLine label="Account" value={financialAccountMatch} />
            </div>
          ) : (
            <span className="text-fg-quaternary">No merchant stats</span>
          )}
        </Table.Cell>
      </Table.Row>
      <Table.Row>
        <Table.Cell colSpan={9} className="px-0 pb-2 pt-0">
          <PriorityControls
            transaction={transaction}
            detailedReason={detailedReason}
            onDetailedReasonChange={(value) => onDetailedReasonChange(transaction.id, value)}
            onSavePriority={onSavePriority}
          />
        </Table.Cell>
      </Table.Row>
    </>
  );
};

interface IStatLineProps {
  label: string;
  value: string;
}

const StatLine = ({ label, value }: IStatLineProps) => (
  <div className="flex justify-between gap-2">
    <span className="text-fg-tertiary">{label}</span>
    <span className="text-fg-primary font-mono tabular-nums">{value}</span>
  </div>
);

interface IDescriptionCellProps {
  transaction: TransactionNode;
}

const DescriptionCell = ({ transaction }: IDescriptionCellProps) => {
  const paired = transaction.pairedTransaction ?? null;
  const pairedAmount = paired ? (typeof paired.amount === 'string' ? parseFloat(paired.amount) : paired.amount) : null;
  return (
    <div className="flex max-w-md select-text flex-col gap-1.5">
      <span className="text-fg-primary text-sm">{transaction.description ?? 'N/A'}</span>
      <MetaGrid>
        <MetaLine label="TransactionId" value={transaction.id} mono copyable />
        {transaction.merchant?.id ? (
          <MetaLine label="MerchantId" value={transaction.merchant.id} mono copyable />
        ) : null}
        {transaction.merchant?.name ? <MetaLine label="MerchantName" value={transaction.merchant.name} /> : null}
        {transaction.financialAccount?.name ? (
          <MetaLine label="FinancialAccount" value={transaction.financialAccount.name} />
        ) : null}
        {transaction.category ? (
          <MetaLine
            label="Category"
            value={`${transaction.category.type}${transaction.category.detailedType ? ` — ${transaction.category.detailedType}` : ''}`}
          />
        ) : null}
      </MetaGrid>
      {paired ? (
        <div className="border-border-primary mt-1 flex flex-col gap-1 border-t pt-2">
          <div className="text-fg-secondary text-xs font-medium uppercase tracking-wider">Paired transaction</div>
          <span className="text-fg-primary text-sm">{paired.description ?? 'N/A'}</span>
          <MetaGrid>
            <MetaLine label="Date" value={formatCalendarDate(paired.date)} />
            {pairedAmount !== null ? <MetaLine label="Amount" value={toCurrency(pairedAmount)} mono /> : null}
            {paired.financialAccount?.name ? <MetaLine label="Account" value={paired.financialAccount.name} /> : null}
            {paired.merchant?.name ? <MetaLine label="Merchant" value={paired.merchant.name} /> : null}
            <MetaLine label="ID" value={paired.id} mono copyable />
          </MetaGrid>
        </div>
      ) : null}
    </div>
  );
};

const MetaGrid = ({ children }: React.PropsWithChildren) => (
  <div className="text-fg-tertiary grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 text-xs">
    {children}
  </div>
);

interface IMetaLineProps {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
}

const MetaLine = ({ label, value, mono = false, copyable = false }: IMetaLineProps) => {
  const { copied, copy } = useClipboard();
  const isCopied = copied === value;
  return (
    <>
      <span className="text-fg-tertiary font-medium">{label}</span>
      <span className={`text-fg-secondary whitespace-nowrap ${mono ? 'font-mono' : ''}`}>{value}</span>
      {copyable ? (
        <button
          type="button"
          aria-label={isCopied ? `Copied ${label}` : `Copy ${label}`}
          title={isCopied ? 'Copied' : 'Copy'}
          onClick={() => {
            void copy(value, value);
          }}
          className="text-fg-quaternary hover:text-fg-primary shrink-0"
        >
          {isCopied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
        </button>
      ) : (
        <span />
      )}
    </>
  );
};

interface IScoreBreakdownProps {
  parts: NonNullable<TransactionNode['anomalyDetection']>['scoreParts'];
}

const ScoreBreakdown = ({ parts }: IScoreBreakdownProps) => {
  if (!parts) {
    return null;
  }
  return (
    <div className="border-border-primary text-fg-tertiary flex flex-col gap-0.5 border-t pt-2 text-xs">
      <div className="text-fg-secondary mb-0.5 font-medium uppercase tracking-wider">Score breakdown</div>
      <BreakdownLine label="Base" value={parts.baseScore} />
      <BreakdownLine label="Merchant adj" value={parts.merchantAdjustment} signed />
      <BreakdownLine label="Timing adj" value={parts.timingAdjustment} signed />
      <BreakdownLine label="Account adj" value={parts.financialAccountAdjustment} signed />
      <div className="border-border-primary mt-0.5 border-t pt-1">
        <BreakdownLine label="Final" value={parts.finalScore} bold />
      </div>
    </div>
  );
};

interface IBreakdownLineProps {
  label: string;
  value: number;
  signed?: boolean;
  bold?: boolean;
}

const BreakdownLine = ({ label, value, signed = false, bold = false }: IBreakdownLineProps) => {
  const tone = !signed
    ? 'text-fg-primary'
    : value > 0
      ? 'text-fg-error-primary'
      : value < 0
        ? 'text-fg-success-primary'
        : 'text-fg-primary';
  const sign = signed && value > 0 ? '+' : '';
  return (
    <div className="flex justify-between gap-2">
      <span>{label}</span>
      <span className={`${tone} font-mono tabular-nums ${bold ? 'font-medium' : ''}`}>
        {sign}
        {value.toFixed(2)}
      </span>
    </div>
  );
};

interface IPriorityControlsProps {
  transaction: TransactionNode;
  detailedReason: string;
  onDetailedReasonChange: (value: string) => void;
  onSavePriority: (
    transactionId: string,
    priority: TransactionAnomalyPriorityEnum,
    detailedReason: string | null
  ) => Promise<void>;
}

const PriorityControls = ({
  transaction,
  detailedReason,
  onDetailedReasonChange,
  onSavePriority,
}: IPriorityControlsProps) => {
  const currentPriority = resolvePriority(transaction);
  return (
    <div className="flex items-center gap-2">
      <span className="text-fg-tertiary shrink-0 text-xs uppercase tracking-wider">Priority</span>
      <NativeSelect
        value={currentPriority}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const next = e.target.value as TransactionAnomalyPriorityEnum;
          if (next !== currentPriority) {
            void onSavePriority(transaction.id, next, detailedReason || null);
          }
        }}
        options={PRIORITY_ORDER.map((p) => ({ label: p, value: p }))}
        className="w-24 shrink-0"
      />
      <TextArea
        value={detailedReason}
        onChange={onDetailedReasonChange}
        placeholder="Detailed reason…"
        rows={1}
        className="min-w-0 flex-1"
      />
    </div>
  );
};

export default TransactionRow;
